import { gql } from "@apollo/client";
import { InitializeBillingDialog } from "@src/features/app/top-nav/InitializeBillingDialog";
import SetActionPermissions from "@src/features/app/top-nav/SetActionPermissions";
import CreateOrgDialog from "@src/features/app/dialogs/orgManagement/CreateOrgDialog";
import UpdateOrgDialog from "@src/features/app/dialogs/orgManagement/UpdateOrgDialog";
import { useOrgForUpdateQuery } from "@src/features/app/dialogs/orgManagement/UpdateOrgDialog.generated";
import Spacer from "@src/lib/ui/Spacer";

import Text from "@src/lib/ui/Text";
import { useIsStaff } from "@src/features/app/hooks/useIsStaff";
import useUser from "@src/features/app/hooks/useUser";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import { useBillingInitialized } from "@src/features/settings/hooks/billing/useBillingInitialized";
import React, { useState } from "react";
import { useResetDemoOrgMutation } from "./AdminTools.generated";
import { launchConfetti } from "@src/lib/utils/confetti";
import { useToast } from "@src/lib/hooks/useToast";
import { Button } from "@src/lib/ui/Button";
import { Dialog, DialogContent } from "@src/lib/ui/Dialog";
import { DropdownMenuItem } from "@src/lib/ui/DropdownMenu";
import ScrollableFlexGrow from "@src/lib/ui/ScrollableFlexGrow";
import SetEmployeeFeatureFlags from "@src/features/app/top-nav/SetEmployeeFeatureFlags";
import GlobeIcon from "@src/lib/icons/18px/globe";
import SetOrgFeatureFlags from "./SetOrgFeatureFlags";
import ImpersonateUser from "./ImpersonateUser";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@src/lib/ui/AlertDialog";

/**
 * Returns a pair { menuItem, dialog } for staff users to access the admin
 * tools. Render `menuItem` inside a `DropdownMenu`, and `dialog` as a sibling
 * of the menu so it survives the menu closing.
 */
export const useAdminToolsMenu = (): {
  menuItem: React.ReactNode;
  dialog: React.ReactNode;
  openAdminTools: () => void;
} => {
  const { isStaff } = useIsStaff();
  const [open, setOpen] = useState(false);

  if (!isStaff) {
    return { menuItem: null, dialog: null, openAdminTools: () => undefined };
  }

  return {
    menuItem: (
      <DropdownMenuItem onSelect={() => setOpen(true)}>
        <GlobeIcon />
        Admin tools
      </DropdownMenuItem>
    ),
    dialog: (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="h-[600px] max-w-[720px] gap-0 overflow-hidden p-0">
          <AdminMenu />
        </DialogContent>
      </Dialog>
    ),
    openAdminTools: () => setOpen(true),
  };
};

const AdminMenu: React.FC = () => {
  const { userActionPermissions } = useActionPermissions();
  const billingInitialized = useBillingInitialized({
    skip: !userActionPermissions?.manageBilling,
  });
  const { user } = useUser();
  const [createOrgOpen, setCreateOrgOpen] = useState(false);
  const [updateOrgOpen, setUpdateOrgOpen] = useState(false);
  const [resetDemoOrgOpen, setResetDemoOrgOpen] = useState(false);
  const { data: orgData } = useOrgForUpdateQuery();
  const org = orgData?.Me?.user.org;
  const [initBillingOpen, setInitBillingOpen] = useState(false);
  const { addToast } = useToast();
  const [resetDemoOrg, { loading: resetLoading }] = useResetDemoOrgMutation({
    onCompleted: () => {
      launchConfetti();
      setResetDemoOrgOpen(false);
      addToast({
        message:
          "Demo org reset begun. Please check back in 1 hour to make sure everything looks right.",
      });
    },
  });
  return (
    <ScrollableFlexGrow className="h-full" innerStyle={{ padding: 16 }}>
      <div>
        <Text variant="body-title" style={{ marginLeft: 4 }}>
          Org: {user?.org?.name} ({user?.org!.id})
        </Text>
        <Spacer size={12} />
        <div className="mb-3 flex flex-row flex-wrap gap-2">
          <Button onClick={() => setCreateOrgOpen(true)} variant="default">
            Add org
          </Button>
          <Button onClick={() => setUpdateOrgOpen(true)} variant="outline">
            Update org
          </Button>
          {!billingInitialized && (
            <>
              <Button
                onClick={() => setInitBillingOpen(true)}
                variant="outline"
              >
                Initialize billing
              </Button>
              {initBillingOpen && (
                <InitializeBillingDialog
                  open={initBillingOpen}
                  onOpenChange={setInitBillingOpen}
                />
              )}
            </>
          )}
          {user?.org?.isDemoOrg && (
            <Button onClick={() => setResetDemoOrgOpen(true)} variant="outline">
              Reset demo org
            </Button>
          )}
        </div>

        <Spacer size={0} />
        <SetActionPermissions />
        <SetOrgFeatureFlags />
        <SetEmployeeFeatureFlags />
        <ImpersonateUser />
      </div>
      {createOrgOpen && (
        <CreateOrgDialog open={createOrgOpen} onOpenChange={setCreateOrgOpen} />
      )}
      {updateOrgOpen && org && (
        <UpdateOrgDialog
          open={updateOrgOpen}
          onOpenChange={setUpdateOrgOpen}
          org={org}
        />
      )}
      {resetDemoOrgOpen && (
        <AlertDialog open={resetDemoOrgOpen} onOpenChange={setResetDemoOrgOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset demo org</AlertDialogTitle>
            </AlertDialogHeader>
            <div className="flex flex-col">
              <Text variant="compact-description" multiline>
                Are you sure you want to reset your demo org? This can take up
                to two hours to complete.
              </Text>
            </div>
            <AlertDialogFooter>
              <Button
                variant="outline"
                onClick={() => setResetDemoOrgOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                loading={resetLoading}
                onClick={async () => {
                  await resetDemoOrg();
                }}
              >
                Yes, reset my demo org
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </ScrollableFlexGrow>
  );
};

export default useAdminToolsMenu;
gql`
  mutation resetDemoOrg {
    resetDemoOrg {
      success
    }
  }
`;
